// Report detail panel (plan.md §4.3).
//
// Three states this has to handle properly, not just the happy one:
//   - active:  corroborate, with a live "confirm again in Xm" countdown
//              rather than a disappearing button, so an ongoing nuisance can
//              be re-confirmed hour by hour;
//   - expired: still resolves and renders, greyed, no corroborate action —
//              a permalink that died within days would undercut sharing;
//   - purged:  the row is genuinely gone (retention), so "no longer
//              available" (plan.md §7).

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconFor } from '../lib/icons.js';
import { corroborate, fetchReportHistory } from '../lib/db.js';
import { getSessionToken } from '../lib/session.js';
import { distanceM } from '../lib/geo.js';
import {
  relativeTime, absoluteDate, absoluteDateTime, timeUntil, countdown, metres,
} from '../lib/format.js';
import { useLocale } from '../lib/i18n.js';
import { localizeCategoryName } from '../lib/taxonomy-i18n.js';

export default function ReportPanel({
  report, loading, notFound, isOwn, onClose, onUpdated,
  position, ensureAccurateFix, corroborationMaxAccuracyM, corroborationRangeM,
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [retryAt, setRetryAt] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [copied, setCopied] = useState(false);
  const [, forceTick] = useState(0);
  const refetchedFor = useRef(null);

  // Re-render once a second only while a countdown is actually showing.
  useEffect(() => {
    if (!retryAt) return undefined;
    const timer = setInterval(() => {
      if (new Date(retryAt).getTime() <= Date.now()) setRetryAt(null);
      forceTick((n) => n + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAt]);

  // Reset per-report UI state when the panel switches reports.
  useEffect(() => {
    setMessage(null);
    setRetryAt(null);
    setShowHistory(false);
    setHistory(null);
    setCopied(false);
    refetchedFor.current = null;
  }, [report?.id]);

  useEffect(() => {
    if (!showHistory || history || !report) return;
    fetchReportHistory(report.id)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [showHistory, history, report]);

  // Corroboration gate (plan.md §4.3): the Me too button only appears for a
  // device with an accurate GPS fix within range of the report.  Computed up
  // here, before the early returns, so the refetch effect below obeys the
  // rules of hooks; the JSX reuses the same values.  The server enforces the
  // same thing (log_corroboration) — this only hides the button.
  const gateOn = corroborationMaxAccuracyM > 0 || corroborationRangeM > 0;
  const isActiveReport = report != null
    && report.is_active !== false
    && new Date(report.expires_at) > new Date();
  const hasFix = position != null && position.accuracy != null;
  const distance = (hasFix && report)
    ? distanceM(position.lat, position.lng, report.lat, report.lng)
    : Infinity;
  const accurate = corroborationMaxAccuracyM > 0
    ? hasFix && position.accuracy <= corroborationMaxAccuracyM
    : hasFix;
  const inRange = corroborationRangeM > 0
    ? distance <= corroborationRangeM + (report?.location_precision_m ?? 0)
    : true;
  const canCorroborate = isActiveReport && !isOwn && (!gateOn || (hasFix && accurate && inRange));

  // The map's coarse locate (enableHighAccuracy:false) may be too coarse to
  // clear the gate.  When a report opens and the current fix would not pass,
  // ask for one high-accuracy fix — at most once per report, so a device that
  // simply has no GPS is not polled in a loop.
  useEffect(() => {
    if (!report || isOwn || !gateOn || canCorroborate) return;
    if (refetchedFor.current === report.id) return;
    refetchedFor.current = report.id;
    ensureAccurateFix?.({ maxAgeMs: 5 * 60_000, maxAccuracyM: corroborationMaxAccuracyM });
  }, [report, isOwn, gateOn, canCorroborate, corroborationMaxAccuracyM, ensureAccurateFix]);

  if (loading) {
    return (
      <aside className="panel" aria-live="polite">
        <div className="panel__loading">{t('panel.loading')}</div>
      </aside>
    );
  }

  if (notFound) {
    return (
      <aside className="panel" role="region" aria-label={t('panel.unavailableAria')}>
        <header className="panel__header">
          <h2 className="panel__title">{t('panel.unavailableTitle')}</h2>
          <button type="button" className="btn btn--icon" onClick={onClose} aria-label={t('nav.close')}>×</button>
        </header>
        <p className="panel__body-text">
          {t('panel.unavailableBody')}
        </p>
      </aside>
    );
  }

  if (!report) return null;

  const isActive = report.is_active !== false && new Date(report.expires_at) > new Date();
  const snapped = report.location_precision_m > 0;
  const categoryName = localizeCategoryName(report.category_slug, locale) ?? report.category_name;

  async function handleCorroborate() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await corroborate({
        reportId: report.id,
        sessionToken: getSessionToken(),
        lat: position?.lat,
        lng: position?.lng,
        accuracyM: position?.accuracy,
      });

      if (result.status === 'ok') {
        setRetryAt(result.retry_at);
        setMessage(t('panel.confirmed'));
        onUpdated?.({
          ...report,
          corroboration_count: result.corroboration_count,
          expires_at: result.expires_at,
        });
        setHistory(null);   // history is stale now
      } else if (result.status === 'cooldown') {
        // Not a dead end: show when they can confirm again (plan.md §4.3).
        setRetryAt(result.retry_at);
        setMessage(null);
      } else if (result.status === 'rate_limited') {
        const mins = Math.ceil((result.retry_after ?? 3600) / 60);
        setRetryAt(null);
        setMessage(t('panel.rateLimited', { count: mins }));
      } else if (result.reason === 'gps_required') {
        setMessage(t('panel.confirmFailGps'));
      } else if (result.reason === 'out_of_range') {
        setMessage(t('panel.confirmFailRange'));
      } else if (result.reason === 'expired') {
        setMessage(t('panel.expiredConfirm'));
      } else {
        setMessage(t('panel.confirmFail'));
      }
    } catch (err) {
      setMessage(err.message ?? t('panel.confirmFailShort'));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    // The address bar is already a valid deep link (plan.md §4.3); this is a
    // convenience wrapper around the same URL, not a separate share step.
    const url = new URL(window.location.href);
    url.searchParams.set('report', report.id);
    try {
      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setMessage(t('panel.copyLinkMsg', { url: url.toString() }));
    }
  }

  const cooldownActive = retryAt && new Date(retryAt).getTime() > Date.now();

  return (
    <aside
      className={`panel${isActive ? '' : ' panel--inactive'}`}
      role="region"
      aria-label={t('panel.ariaLabel', { name: categoryName })}
    >
      <header className="panel__header">
        <span className="panel__icon" aria-hidden="true">
          {iconFor(report.category_icon, report.collection_slug)}
        </span>
        <div className="panel__heading">
          <h2 className="panel__title">{categoryName}</h2>
          <p className="panel__subtitle">
            {relativeTime(report.created_at)}
            {report.distance_m != null && <> · {t('panel.away', { distance: metres(report.distance_m) })}</>}
          </p>
        </div>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label={t('nav.close')}>×</button>
      </header>

      {!isActive && (
        <p className="panel__expired" role="note">
          {t('panel.expiredOn', { date: absoluteDate(report.expires_at) })}
        </p>
      )}

      <dl className="stats">
        <div className="stats__item">
          <dt>{t('panel.statConfirmations')}</dt>
          <dd>{report.corroboration_count ?? 0}</dd>
        </div>
        <div className="stats__item">
          <dt>{isActive ? t('panel.statDisappearsIn') : t('panel.statExpired')}</dt>
          <dd>{isActive ? timeUntil(report.expires_at) : relativeTime(report.expires_at)}</dd>
        </div>
      </dl>

      {snapped && (
        <p className="panel__precision" role="note">
          {t('panel.precision', { m: report.location_precision_m })}
        </p>
      )}

      <div className="panel__actions">
        {canCorroborate && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleCorroborate}
            disabled={busy || cooldownActive}
          >
            {busy ? t('panel.confirming')
              : cooldownActive ? t('panel.confirmAgainIn', { time: countdown(retryAt) })
              : t('panel.meToo')}
          </button>
        )}
        <button type="button" className="btn btn--ghost" onClick={copyLink}>
          {copied ? t('panel.linkCopied') : t('panel.copyLink')}
        </button>
      </div>

      {isActive && !isOwn && !canCorroborate && (
        <p className="panel__hint">
          {t('panel.meTooGated', { range: metres(corroborationRangeM) })}
        </p>
      )}

      {cooldownActive && !message && (
        <p className="panel__hint">
          {t('panel.cooldownHint', { time: countdown(retryAt) })}
        </p>
      )}

      {message && <p className="panel__message" role="status">{message}</p>}

      <button
        type="button"
        className="panel__disclosure"
        aria-expanded={showHistory}
        onClick={() => setShowHistory((v) => !v)}
      >
        {report.corroboration_count > 0
          ? t('panel.historyWithCount', { count: report.corroboration_count })
          : t('panel.history')}
        <span className="picker__chevron" data-open={showHistory}>›</span>
      </button>

      {showHistory && (
        <div className="history">
          {history === null && <p className="history__empty">{t('panel.historyLoading')}</p>}
          {history?.length === 0 && (
            <p className="history__empty">{t('panel.historyEmpty')}</p>
          )}
          {history?.length > 0 && (
            <ol className="history__list">
              <li className="history__item history__item--origin">
                <span className="history__time">{absoluteDateTime(report.created_at)}</span>
                <span className="history__what">{t('panel.historyReported')}</span>
              </li>
              {history.map((entry, index) => (
                <li className="history__item" key={`${entry.created_at}-${index}`}>
                  <span className="history__time">{absoluteDateTime(entry.created_at)}</span>
                  <span className="history__what">{t('panel.historyConfirmed')}</span>
                </li>
              ))}
            </ol>
          )}
          <p className="history__note">
            {t('panel.historyNote')}
          </p>
        </div>
      )}
    </aside>
  );
}
